<script setup lang="ts">
import { computed } from "vue";
import type { CountdownStyleProps } from "./shared";
import { resolveLayoutTokens, speedDuration } from "./shared";

const props = defineProps<CountdownStyleProps>();
const layout = computed(() => resolveLayoutTokens(props.block, props.viewport));
const tickMs = computed(() => speedDuration(props.block.animationSpeed, 500));

const containerStyle = computed(
  () =>
    ({
      background:
        props.block.backgroundColor ||
        "linear-gradient(180deg, #1f2226 0%, #0e1013 100%)",
      fontFamily:
        props.block.fontFamily || "'Bebas Neue', 'IBM Plex Mono', monospace",
      padding: `${layout.value.padding}px`,
      gap: `${layout.value.gap}px`,
      flexDirection: layout.value.stack ? "column" : "row",
      minHeight: layout.value.fullscreen ? "60vh" : undefined,
      "--cd-tick": `${tickMs.value}ms`,
    }) as Record<string, string | number | undefined>,
);

const cardSize = computed(() => {
  const base = props.block.digitFontSize * layout.value.digitScale;
  return {
    width: `${base * 2.4}px`,
    height: `${base * 2.4}px`,
    fontSize: `${base}px`,
  };
});
</script>

<template>
  <div class="cd-ind" :style="containerStyle">
    <div class="cd-ind__stripes" aria-hidden="true" />

    <div
      v-for="(seg, i) in segments"
      :key="seg.key"
      class="cd-ind__cell"
      :style="{
        width: cardSize.width,
        height: cardSize.height,
        animationDelay: `${i * 90}ms`,
      }"
    >
      <div class="cd-ind__rivet cd-ind__rivet--tl" />
      <div class="cd-ind__rivet cd-ind__rivet--tr" />
      <div class="cd-ind__rivet cd-ind__rivet--bl" />
      <div class="cd-ind__rivet cd-ind__rivet--br" />

      <div class="cd-ind__panel">
        <span
          class="cd-ind__digit"
          :key="seg.value"
          :style="{
            fontSize: cardSize.fontSize,
            color: block.digitColor || '#fbbf24',
          }"
        >
          {{ seg.value }}
        </span>
      </div>
      <div
        class="cd-ind__label"
        :style="{
          fontSize: `${block.labelFontSize}px`,
          color: block.labelColor || 'rgba(255,255,255,0.7)',
        }"
      >
        {{ seg.label }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.cd-ind {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  overflow: hidden;
  isolation: isolate;
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.cd-ind__stripes {
  position: absolute;
  inset: 0;
  background: repeating-linear-gradient(
    45deg,
    rgba(251, 191, 36, 0.06) 0,
    rgba(251, 191, 36, 0.06) 14px,
    rgba(0, 0, 0, 0) 14px,
    rgba(0, 0, 0, 0) 28px
  );
  pointer-events: none;
}
.cd-ind__cell {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #2c3036, #14171b);
  border: 1px solid rgba(0, 0, 0, 0.6);
  border-radius: 4px;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.12),
    inset 0 -1px 0 rgba(0, 0, 0, 0.6),
    0 6px 14px rgba(0, 0, 0, 0.55);
  animation: cd-ind-slide 600ms cubic-bezier(0.16, 1, 0.3, 1) backwards;
}
@keyframes cd-ind-slide {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.cd-ind__rivet {
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, #5d626a, #1a1d22);
  box-shadow:
    inset 0 -1px 1px rgba(0, 0, 0, 0.6),
    0 1px 0 rgba(255, 255, 255, 0.06);
}
.cd-ind__rivet--tl {
  top: 6px;
  left: 6px;
}
.cd-ind__rivet--tr {
  top: 6px;
  right: 6px;
}
.cd-ind__rivet--bl {
  bottom: 30%;
  left: 6px;
}
.cd-ind__rivet--br {
  bottom: 30%;
  right: 6px;
}
.cd-ind__panel {
  margin: 8px 14px 4px;
  padding: 6px 14px;
  background: #0a0c0f;
  border-radius: 3px;
  border: 1px solid #000;
  box-shadow: inset 0 0 12px rgba(0, 0, 0, 0.85);
}
.cd-ind__digit {
  font-weight: 700;
  letter-spacing: 0.08em;
  font-variant-numeric: tabular-nums;
  text-shadow:
    0 0 6px rgba(251, 191, 36, 0.6),
    0 0 14px rgba(251, 146, 60, 0.4);
  display: inline-block;
  animation: cd-ind-flash var(--cd-tick, 500ms) ease-out;
}
@keyframes cd-ind-flash {
  0% {
    opacity: 0.3;
    filter: brightness(2);
  }
  100% {
    opacity: 1;
    filter: brightness(1);
  }
}
.cd-ind__label {
  text-transform: uppercase;
  letter-spacing: 0.3em;
  font-weight: 600;
  margin-top: 4px;
}
</style>
